import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUINoRecordComponent } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { SCUISearchServiceAddToListModal } from './components';
import { SCUIBreadCrumb, SCUITextFieldWithSelectBox } from '../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import {
    SCUICustomDataGrid,
    SCUIGeneralDrawer,
    SCUIProductSearchService,
    SCUISearchServiceDrawer,
} from '../../atomic/molecules';
import { Stack, Button, Box, Fade, LinearProgress, IconButton, Chip, Tooltip, Typography, alpha } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { SearchServiceCommand } from '../../services/command';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import DownloadIcon from '@mui/icons-material/Download';
import LayersIcon from '@mui/icons-material/Layers';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import searchServiceCommand from '../../services/command/searchService/searchServiceCommand.services';
import { downloadExcel } from '../../utils/excelFileHelper';
import Swal from 'sweetalert2';
import { SS } from './searchService.classes';

export const SearchService: FC<any> = () => {
    const [listModalOpen, setListModalOpen] = useState(false);
    const query = new URLSearchParams(useLocation().search);
    const queryType = query.get('queryType');
    const valbyUrl = query.get('val');
    const [statusFilter, setStatusFilter] = useState('1');
    const [loading, setLoading] = React.useState(false);
    const [optionId, setOptionId] = useState<any>();
    const { t } = useTranslation();
    const [optionTextValue, setOptionTextValue] = useState('');

    const searchTypeOptions = [
        { value: '1', text: t('searchService.optionId') },
        { value: '2', text: t('searchService.specialAndColorCode') },
        { value: '3', text: t('searchService.barcode') },
        { value: '4', text: t('searchService.ProductOptionSizeRef') },
        { value: '5', text: t('searchService.specialCode') },
    ];

    const getSearchTypeText = () => {
        return searchTypeOptions.find((option) => option.value === statusFilter)?.text || t('searchService.optionId');
    };
    const handleCloseModal = () => {
        setListModalOpen(false);
    };
    const [datas, setDatas] = React.useState<any>();
    const onDrawerOpen = () => setDrawerOpen(true);
    const onDrawerClose = () => setDrawerOpen(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerOpenStock, setDrawerOpenStock] = useState(false);
    const onDrawerOpenStock = () => setDrawerOpenStock(true);
    const onDrawerCloseStock = () => setDrawerOpenStock(false);

    // React state for searchKeyArr
    const [searchKeyArr, setSearchKeyArr] = useState<string[]>([]);

    const getParams = useCallback(
        (keys: string[] = searchKeyArr) => {
            return {
                searchType: statusFilter,
                searchKeys: keys,
            };
        },
        [statusFilter, searchKeyArr]
    );

    const GetOptionIdByColorCode = useCallback(
        async (keys?: string[]) => {
            const paramss = getParams(keys);
            if (paramss.searchType !== '0') {
                try {
                    const res = await SearchServiceCommand.PostGetSearchInformation(paramss);
                    if (res.length === 1) {
                        setOptionId(res[0].optionId);
                    }
                    setDatas(res);
                } catch (error: any) {
                    console.error('Error in GetOptionIdByColorCode:', error);
                    const searchTypeText = getSearchTypeText();
                    const errorMessage = error?.response?.data?.Message || error?.message;

                    if (errorMessage) {
                        Swal.fire({
                            icon: 'error',
                            title: t('general.error'),
                            html: `<strong>${t('searchService.searchTypeLabel', { searchType: searchTypeText })}</strong><br/><br/>${errorMessage}`,
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: t('general.error'),
                            text: t('searchService.noProductFoundForType', { searchType: searchTypeText }),
                        });
                    }
                    setDatas(null);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        },
        [getParams, t]
    );
    useEffect(() => {
        if (valbyUrl) {
            setOptionTextValue(valbyUrl);
            const updatedKeys = [...searchKeyArr, valbyUrl];
            setSearchKeyArr(updatedKeys);
            GetOptionIdByColorCode(updatedKeys);
            if (queryType === 'product') {
                onDrawerOpen();
            } else {
                onDrawerOpenStock();
            }
        }
    }, [valbyUrl, queryType]);

    // Memoized element array derived from datas
    const element = useMemo(() => {
        if (!datas) return [];
        return datas.map((item: any) => item.optionId);
    }, [datas]);

    const downloadExcelHandler = useCallback(async () => {
        if (element.length === 0) {
            Swal.fire(t('general.noDataToExport'), '', 'warning');
            return;
        }

        const params = {
            WarehouseGroupId: 1,
            CultureCode: 'tr-TR',
            OptionIds: element,
        };

        try {
            const res = await searchServiceCommand.GetOptionSummary(params);
            if (res?.results) {
                const parseData = res.results.map((item: any) => ({
                    specialCode: item.specialCode || '-',
                    colorCode: item.colorCode || '-',
                    modelId: item.modelId || '-',
                    optionId: item.optionId || '-',
                    size: item.size || '-',
                    productId: item.productId || '-',
                    stock: item.stock || 0,
                }));

                if (parseData.length > 0) {
                    downloadExcel({
                        excelDownloadData: parseData,
                        excelDownloadFolderName: 'StockList',
                    });
                } else {
                    Swal.fire(t('general.anErrorOccured'), '', 'error');
                }
            }
        } catch (error) {
            console.error('Excel download error:', error);
            Swal.fire(t('general.anErrorOccured'), '', 'error');
        }
    }, [element, t]);

    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'id', flex: 1, hide: true },
        {
            field: 'searchKey',
            headerName: t('searchService.searchKey'),
            width: 250,
            renderCell: (params) => (
                <Typography
                    className={SS.cellSearchKey}
                    variant="body2"
                    sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.82rem', fontStyle: 'italic' }}
                >
                    {params.value || '—'}
                </Typography>
            ),
        },
        {
            field: 'optionId',
            headerName: t('searchService.optionId'),
            width: 250,
            renderCell: (params) => (
                <Chip
                    className={SS.cellOptionIdChip}
                    label={params.value}
                    size="small"
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        bgcolor: '#f0f4ff',
                        color: '#3730a3',
                        border: '1px solid #c7d2fe',
                        fontSize: '0.8rem',
                        letterSpacing: '0.02em',
                    }}
                />
            ),
        },
        {
            field: 'product',
            headerName: t('searchService.searchProduct'),
            align: 'center',
            headerAlign: 'center',
            width: 250,
            flex: 1,
            renderCell: (params) => {
                const onclick = () => {
                    setOptionId(params.row.optionId);
                    onDrawerOpen();
                };
                return (
                    <Tooltip title={t('searchService.searchProduct')} arrow placement="top">
                        <IconButton
                            className={SS.cellSearchProductButton}
                            onClick={onclick}
                            size="small"
                            sx={{
                                color: '#6366f1',
                                bgcolor: alpha('#6366f1', 0.08),
                                border: '1.5px solid',
                                borderColor: alpha('#6366f1', 0.22),
                                borderRadius: 1.5,
                                p: 0.75,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: alpha('#6366f1', 0.16),
                                    borderColor: alpha('#6366f1', 0.45),
                                    transform: 'scale(1.1)',
                                    boxShadow: `0 3px 12px ${alpha('#6366f1', 0.3)}`,
                                },
                            }}
                        >
                            <FindInPageIcon className={SS.cellSearchProductIcon} sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: 'detay',
            headerName: t('searchService.stockSearch'),
            align: 'center',
            headerAlign: 'center',
            width: 250,
            flex: 1,
            renderCell: (params) => {
                const onclick = () => {
                    setOptionId(params.row.optionId);
                    onDrawerOpenStock();
                };
                return (
                    <Tooltip title={t('searchService.stockSearch')} arrow placement="top">
                        <IconButton
                            className={SS.cellSearchStockButton}
                            onClick={onclick}
                            size="small"
                            sx={{
                                color: '#059669',
                                bgcolor: alpha('#059669', 0.08),
                                border: '1.5px solid',
                                borderColor: alpha('#059669', 0.22),
                                borderRadius: 1.5,
                                p: 0.75,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: alpha('#059669', 0.16),
                                    borderColor: alpha('#059669', 0.45),
                                    transform: 'scale(1.1)',
                                    boxShadow: `0 3px 12px ${alpha('#059669', 0.3)}`,
                                },
                            }}
                        >
                            <LayersIcon className={SS.cellSearchStockIcon} sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];
    const listele = useCallback(
        async (valbyUrl?: any) => {
            const newSearchKeys = optionTextValue
                .trim()
                .split('\n')
                .filter((item) => item.trim() !== ''); // Filter out empty strings

            if (newSearchKeys.length === 0 && valbyUrl === undefined) {
                const searchTypeText = getSearchTypeText();
                Swal.fire({
                    icon: 'warning',
                    title: t('general.warning'),
                    text: t('searchService.pleaseEnterSearchKeyForType', { searchType: searchTypeText }),
                });
                return;
            }

            setLoading(true);
            try {
                const updatedKeys = [...searchKeyArr, ...newSearchKeys];
                setSearchKeyArr(updatedKeys);
                await GetOptionIdByColorCode(updatedKeys);
            } catch (error: any) {
                console.error('Error in listele:', error);
                const searchTypeText = getSearchTypeText();
                const errorMessage = error?.response?.data?.Message || error?.message;

                if (errorMessage) {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        html: `<strong>${t('searchService.searchTypeLabel', { searchType: searchTypeText })}</strong><br/><br/>${errorMessage}`,
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('searchService.noProductFoundForType', { searchType: searchTypeText }),
                    });
                }
                setLoading(false);
            }
        },
        [optionTextValue, searchKeyArr, GetOptionIdByColorCode, t]
    );

    return (
        <SCUIContent className={SS.page}>
            <Box className={SS.progressSection} sx={{ mb: 0.5 }}>
                <Fade in={loading} unmountOnExit>
                    <LinearProgress
                        className={SS.progressBar}
                        sx={{
                            borderRadius: 1,
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)',
                            },
                        }}
                    />
                </Fade>
            </Box>

            <div className={SS.modalSection}>
                <SCUISearchServiceAddToListModal
                    open={listModalOpen}
                    onClose={handleCloseModal}
                    orderNo={optionTextValue}
                    setOrderNo={setOptionTextValue}
                    listele={listele}
                    text={t('general.shippingID')}
                />
            </div>

            <SCUIGeneralDrawer
                open={drawerOpen}
                anchor="right"
                width={'50%'}
                paperClassName={SS.productDrawerPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerOpen(false);
                    }
                }}
            >
                <div className={SS.productDrawerContent}>
                    {drawerOpen && optionId && <SCUIProductSearchService id={optionId} onClose={onDrawerClose} />}
                </div>
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={drawerOpenStock}
                anchor="right"
                width={'50%'}
                paperClassName={SS.stockDrawerPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerOpenStock(false);
                    }
                }}
            >
                <div className={SS.stockDrawerContent}>
                    <SCUISearchServiceDrawer id={optionId} onClose={onDrawerCloseStock} />
                </div>
            </SCUIGeneralDrawer>

            <div className={SS.breadcrumbSection}>
                <SCUIBreadCrumb title={t('dashboard.searchProduct')} componentLink={'SearchService'} />
            </div>

            <Stack
                className={SS.actionsRow}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2.5 }}
            >
                <Box className={SS.searchFieldWrap} sx={{ marginRight: '16px' }}>
                    <SCUITextFieldWithSelectBox
                        placeholder={' '}
                        selectList={searchTypeOptions}
                        className={SS.searchFieldPaper}
                        selectClassName={SS.searchFieldSelect}
                        inputClassName={SS.searchFieldInput}
                        iconButtonClassName={SS.searchFieldIconButton}
                        onChangeSelect={(event: any) => {
                            setStatusFilter(event.target.value as string);
                        }}
                        selectValue={statusFilter}
                        onChangeTextField={(event: any) => {
                            setOptionTextValue(event.target.value);
                        }}
                        onClickButton={() => listele()}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                listele();
                            }
                        }}
                    />
                </Box>

                <Button
                    className={SS.batchSearchButton}
                    variant="outlined"
                    size="medium"
                    startIcon={<PlaylistAddIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={() => setListModalOpen(true)}
                    sx={{
                        borderColor: '#6366f1',
                        color: '#6366f1',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        padding: '8px 16px !important',
                        borderRadius: 2,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: alpha('#6366f1', 0.08),
                            borderColor: '#4f46e5',
                            boxShadow: `0 2px 10px ${alpha('#6366f1', 0.25)}`,
                            transform: 'translateY(-1px)',
                        },
                    }}
                >
                    {t('general.batchSearch')}
                </Button>

                {datas && (
                    <Tooltip title={t('general.downloadExcel') || 'Excel İndir'} arrow placement="top">
                        <IconButton
                            className={SS.excelDownloadButton}
                            onClick={downloadExcelHandler}
                            sx={{
                                color: '#059669',
                                bgcolor: alpha('#059669', 0.08),
                                border: '1.5px solid',
                                borderColor: alpha('#059669', 0.22),
                                borderRadius: 2,
                                p: 1,
                                flexShrink: 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: alpha('#059669', 0.15),
                                    borderColor: alpha('#059669', 0.45),
                                    boxShadow: `0 3px 12px ${alpha('#059669', 0.3)}`,
                                    transform: 'scale(1.08)',
                                },
                            }}
                        >
                            <DownloadIcon className={SS.excelDownloadIcon} sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <div className={SS.dataGridSection}>
                {datas ? (
                    <div className={SS.dataGridWrap}>
                        <SCUICustomDataGrid randomId rows={datas} columns={columns} allrecord hideFooter />
                    </div>
                ) : (
                    <div className={SS.noRecordWrap}>
                        <SCUINoRecordComponent />
                    </div>
                )}
            </div>
        </SCUIContent>
    );
};
